import { z } from 'zod';

export const registrationActivityKeySchema = z.enum([
  'medical_questionnaire',
  'student_liability_release',
  'online_module',
  'online_exam',
  'completion_form',
  'certificate',
]);

export const onboardingActivityKeySchema = z.enum([
  'onboarding_welcome',
  'onboarding_logistics_sea_conditions',
  'onboarding_course_organization',
  'onboarding_equipment',
  'onboarding_resolve_issues',
]);

export const educationActivityKeySchema = z.union([
  registrationActivityKeySchema,
  onboardingActivityKeySchema,
]);

const sourceChildIdentifiersSchema = z.object({
  program_id: z.string().nullable(),
  education_module_ids: z.array(z.string()),
  slide_ids: z.array(z.string()),
  document_id: z.string().nullable(),
  education_log_id: z.string().nullable(),
  attempt_id: z.string().nullable(),
  completion_form_id: z.string().nullable(),
  certificate_document_id: z.string().nullable(),
});

export const educationModulePlayerMediaSchema = z.object({
  state: z.enum(['ready', 'unavailable']),
  url: z.url().nullable(),
  content_type: z.string().nullable(),
  expires_at: z.iso.datetime().nullable(),
  refresh: z.literal('activity'),
});

const playerElementIdentitySchema = {
  id: z.string().nullable(),
  index: z.number().nullable(),
};

export const educationModulePlayerElementSchema = z.discriminatedUnion('type', [
  z.object({ ...playerElementIdentitySchema, type: z.literal('text'), text: z.string() }),
  z.object({ ...playerElementIdentitySchema, type: z.literal('rich_text'), html: z.string() }),
  z.object({
    ...playerElementIdentitySchema,
    type: z.literal('title_and_subtitle'),
    title: z.string().nullable(),
    subtitle: z.string().nullable(),
    style: z.string().nullable(),
  }),
  z.object({
    ...playerElementIdentitySchema,
    type: z.literal('image'),
    media: educationModulePlayerMediaSchema,
    alt: z.string().nullable(),
    link_url: z.url().nullable(),
  }),
  z.object({
    ...playerElementIdentitySchema,
    type: z.literal('video'),
    media: educationModulePlayerMediaSchema,
    poster: educationModulePlayerMediaSchema,
  }),
  z.object({
    ...playerElementIdentitySchema,
    type: z.literal('background_image'),
    media: educationModulePlayerMediaSchema,
  }),
  z.object({
    ...playerElementIdentitySchema,
    type: z.literal('quote'),
    text: z.string(),
    source: z.string().nullable(),
  }),
  z.object({
    ...playerElementIdentitySchema,
    type: z.literal('document_action'),
    document_id: z.string().min(1).nullable(),
    document_type: z.enum([
      'medical_questionnaire',
      'liability_release',
      'course_completion_form',
      'certificate',
    ]),
    state: z.enum(['not_started', 'draft', 'in_review', 'approved', 'not_valid', 'unavailable']),
    context: z.object({
      kind: z.enum([
        'registration',
        'onboarding_assignment',
        'training_program',
        'direct_assignment',
      ]),
      id: z.string().min(1),
    }),
    display: z.object({ label: z.string().min(1) }),
    permitted_actions: z.array(
      z.enum(['provision', 'read', 'update_responses', 'submit', 'review'])
    ),
  }),
  z.object({
    ...playerElementIdentitySchema,
    type: z.literal('exam_question'),
    question: z.string(),
    answers: z.array(
      z.object({
        id: z.string().nullable(),
        index: z.number().nullable(),
        text: z.string().nullable(),
        media: educationModulePlayerMediaSchema,
      })
    ),
  }),
  z.object({
    ...playerElementIdentitySchema,
    type: z.literal('contextual'),
    context_type: z.string().min(1),
    state: z.literal('unavailable'),
    message: z.string().min(1),
  }),
]);

export const educationModulePlayerSchema = z.object({
  contract_version: z.literal('education-module-player.v1'),
  module_id: z.string().min(1),
  initial_slide_id: z.string().min(1),
  completion_behavior: z.object({
    final_slide_effect: z.literal('slide_completed'),
    module_completion: z.literal('not_implied'),
  }),
  locale: z.object({
    requested: z.string().min(1),
    resolved: z.string().min(1),
    fallback_used: z.boolean(),
  }),
  slides: z
    .array(
      z.object({
        slide_id: z.string().min(1),
        title: z.string().nullable(),
        type: z.string().nullable(),
        index: z.number().nullable(),
        duration_seconds: z.number().nonnegative().nullable(),
        cover: educationModulePlayerMediaSchema,
        completed: z.boolean(),
        elements: z.array(educationModulePlayerElementSchema),
        navigation: z.object({
          previous_slide_id: z.string().nullable(),
          next_slide_id: z.string().nullable(),
          is_first: z.boolean(),
          is_last: z.boolean(),
        }),
        progress_action: z.object({
          operation_id: z.enum([
            'updateUserProfileOnboardingEducationActivitySlideProgress',
            'updateUserProfileRegistrationEducationActivitySlideProgress',
          ]),
          method: z.literal('POST'),
          path: z.string().startsWith('/api/v1/user-profiles/'),
          target_kind: z.literal('activity_slide'),
          effect: z.literal('slide_completed'),
          completes_module: z.literal(false),
          body: z.object({ status: z.literal('completed') }),
          idempotency_key_required: z.literal(true),
        }),
      })
    )
    .min(1),
});

export const educationActivityResponseSchema = z.object({
  data: z.object({
    activity_key: educationActivityKeySchema,
    context: z.discriminatedUnion('kind', [
      z.object({
        kind: z.literal('certification_registration'),
        profile_id: z.string().min(1),
        registration_id: z.string().min(1),
      }),
      z.object({
        kind: z.literal('onboarding_assignment'),
        profile_id: z.string().min(1),
        assignment_id: z.string().nullable(),
        program_id: z.string().nullable(),
      }),
    ]),
    state: z.enum(['setup', 'available', 'in_progress', 'blocked', 'completed', 'unavailable']),
    eligibility: z.object({
      eligible: z.boolean(),
      blocker_code: z
        .enum([
          'ACTIVITY_NOT_ASSIGNED',
          'ACTIVITY_STATE_UNAVAILABLE',
          'ONBOARDING_ASSIGNMENT_MISSING',
          'ONBOARDING_ACTIVITY_MAPPING_UNAVAILABLE',
        ])
        .nullable(),
      blocker_message: z.string().nullable(),
    }),
    source_child_identifiers: sourceChildIdentifiersSchema,
    resume: z
      .object({
        slide_id: z.string().min(1).nullable(),
        slide_log_id: z.string().nullable(),
      })
      .nullable(),
    progress: z
      .object({
        completed_items: z.number().int().nonnegative(),
        total_items: z.number().int().nonnegative(),
        percentage_basis_points: z.number().int().min(0).max(10_000),
      })
      .nullable(),
    attempt: z
      .object({
        attempts_used: z.number().int().nonnegative().nullable(),
        attempts_allowed: z.number().int().nonnegative().nullable(),
        result: z.string().nullable(),
      })
      .nullable(),
    version: z.object({
      resource_version: z.string().min(1).nullable(),
      updated_at: z.iso.datetime().nullable(),
    }),
    permitted_actions: z.array(z.enum(['open', 'review'])),
    player: educationModulePlayerSchema.nullable().optional().default(null),
  }),
  meta: z.object({
    source: z.literal('db'),
    sourceDetail: z.literal('postgres:education-activity'),
  }),
});

export type EducationActivity = z.infer<typeof educationActivityResponseSchema>['data'];
export type EducationActivityKey = z.infer<typeof educationActivityKeySchema>;
export type EducationModulePlayer = z.infer<typeof educationModulePlayerSchema>;
export type EducationModulePlayerElement = z.infer<typeof educationModulePlayerElementSchema>;
export type EducationModulePlayerMedia = z.infer<typeof educationModulePlayerMediaSchema>;
export type RegistrationActivityKey = z.infer<typeof registrationActivityKeySchema>;
export type OnboardingActivityKey = z.infer<typeof onboardingActivityKeySchema>;
